/**
 * Map Reveal deck state ↔ remote v1 `state` payload and apply v1 commands.
 */

import { remoteTtsVerbose, remoteTtsWarn } from '../../../lib/remote-tts-log';
import { parseCodewordInner } from '../../tts-queue';
import { REMOTE_V1_SESSION_MODE_ENDED, type RemoteV1CommandPayload, type RemoteV1StatePayload } from './protocol';

export interface RevealDeckSnapshot {
  indices: RevealStateIndices;
  totalSlides: number;
  title: string;
  notesPlain: string;
  ttsLang: string;
  notesSourceLang: string;
  highlightColorRgb?: string;
}

/**
 * Plain speaker notes for the current slide, aligned with reveal.js `plugin/notes` (`post()`):
 * fragment `aside.notes` / `data-notes` first, then slide `aside.notes`, then slide `data-notes`.
 */
export function getRevealSpeakerNotesPlainTextFromSlide(slide: HTMLElement | null): string {
  if (!slide) return '';
  if (slide.hasAttribute?.('data-tts-skip')) return '';

  const fragmentEl = slide.querySelector('.current-fragment') as HTMLElement | null;
  if (fragmentEl) {
    const fragAside = fragmentEl.querySelector('aside.notes');
    if (fragAside) {
      return (fragAside.textContent || '').trim();
    }
    const fragAttr = fragmentEl.getAttribute('data-notes');
    if (fragAttr != null && String(fragAttr).trim()) {
      return String(fragAttr).trim();
    }
  }

  const asides = slide.querySelectorAll('aside.notes');
  if (asides.length > 0) {
    return Array.from(asides)
      .map((el) => (el.textContent || '').trim())
      .filter(Boolean)
      .join('\n');
  }

  const slideAttr = slide.getAttribute('data-notes');
  if (slideAttr != null && String(slideAttr).trim()) {
    return String(slideAttr).trim();
  }

  return '';
}

export function getRevealDeckSnapshot(_slidesVersion: string): RevealDeckSnapshot | null {
  if (typeof Reveal === 'undefined') return null;
  const state = Reveal.getState?.() ?? Reveal.getIndices?.();
  if (!state) return null;
  const slide = Reveal.getCurrentSlide?.() ?? null;
  let title = '';
  let notesPlain = '';
  if (slide) {
    const heading = slide.querySelector('h1, h2, h3, h4, h5, h6');
    title = heading ? (heading.textContent || '').trim().substring(0, 80) : '';
    notesPlain = getRevealSpeakerNotesPlainTextFromSlide(slide);
  }
  const reveal = document.querySelector('.reveal');
  let notesSourceLang = 'en';
  if (reveal) {
    const override = reveal.getAttribute('data-notes-lang');
    if (override && override.length >= 2) notesSourceLang = override.toLowerCase().substring(0, 2);
    else {
      const lang = document.documentElement.getAttribute('lang') || '';
      if (lang.length >= 2) notesSourceLang = lang.toLowerCase().substring(0, 2);
    }
  }
  const tts = (window as unknown as {
    RevealNotesTTS?: { getStoredSettings?: () => { lang?: string } };
  }).RevealNotesTTS;
  const ttsLang = tts?.getStoredSettings?.()?.lang || '';
  const fromRoot = getComputedStyle(document.documentElement).getPropertyValue('--notes-tts-highlight-rgb').trim();
  return {
    indices: {
      indexh: Number(state.indexh || state.h || 0),
      indexv: Number(state.indexv || state.v || 0),
      indexf: Number(state.indexf || state.f || 0)
    },
    totalSlides: Reveal.getTotalSlides?.() || 0,
    title,
    notesPlain,
    ttsLang,
    notesSourceLang,
    highlightColorRgb: fromRoot || undefined
  };
}

/**
 * True on the deck’s final slide (including bottom of a vertical stack). Uses Reveal’s API when available.
 */
export function isRevealOnLastSlide(): boolean {
  if (typeof Reveal === 'undefined') return false;
  if (typeof Reveal.isLastSlide === 'function') {
    try {
      return !!Reveal.isLastSlide();
    } catch {
      return false;
    }
  }
  const snap = getRevealDeckSnapshot('');
  if (!snap || snap.totalSlides <= 0) return false;
  return snap.indices.indexh >= snap.totalSlides - 1;
}

export interface RevealStateIndices {
  indexh: number;
  indexv: number;
  indexf: number;
}

export function buildRemoteV1StateFromReveal(params: {
  version: number;
  indices: RevealStateIndices;
  totalSlides: number;
  title: string;
  notesPlain: string;
  slidesVersion: string;
  highlightColorRgb?: string;
  ttsLang?: string;
  notesSourceLang?: string;
}): RemoteV1StatePayload {
  return {
    slide: {
      h: params.indices.indexh,
      v: params.indices.indexv,
      f: params.indices.indexf
    },
    mode: 'live',
    meta: {
      title: params.title,
      notes_plain: params.notesPlain,
      slides_version: params.slidesVersion,
      total_slides: params.totalSlides,
      highlight_color_rgb: params.highlightColorRgb,
      tts_lang: params.ttsLang,
      notes_source_lang: params.notesSourceLang
    },
    version: params.version,
    updated_at: new Date().toISOString()
  };
}

/** Last retained `state` on MQTT when the presenter ends the session (viewers show “ended” UI). */
export function buildRemoteV1SessionEndedState(lastLive: RemoteV1StatePayload | null): RemoteV1StatePayload {
  const now = new Date().toISOString();
  if (!lastLive) {
    return {
      slide: { h: 0, v: 0, f: 0 },
      mode: REMOTE_V1_SESSION_MODE_ENDED,
      meta: {
        title: 'Session ended',
        notes_plain: '',
        slides_version: '',
        total_slides: 0
      },
      version: 1,
      updated_at: now
    };
  }
  return {
    ...lastLive,
    mode: REMOTE_V1_SESSION_MODE_ENDED,
    version: lastLive.version + 1,
    updated_at: now
  };
}

export function remoteV1StateToLegacySlideShape(state: RemoteV1StatePayload): {
  indexh: number;
  indexv: number;
  indexf: number;
  total: number;
  title: string;
  notes: string;
  highlightColorRgb?: string;
} {
  const m = state.meta || {};
  return {
    indexh: state.slide.h,
    indexv: state.slide.v,
    indexf: state.slide.f,
    total: typeof m.total_slides === 'number' ? m.total_slides : 0,
    title: m.title || '',
    notes: m.notes_plain || '',
    highlightColorRgb: m.highlight_color_rgb
  };
}

/**
 * `serializeQueueItemToCodeword` emits full `[[action:value]]`; RevealNotesTTS / lectura `parseOneCodeword`
 * expect the inner segment only. Strip once so MQTT `TTS_RUN` matches speaker-note clicks on the deck.
 */
export function normalizeCodewordForTtsRun(codeword: string): string {
  const s = String(codeword || '').trim();
  if (s.startsWith('[[') && s.endsWith(']]')) {
    return s.slice(2, -2).trim();
  }
  return s;
}

async function runTtsCodeword(codeword: string): Promise<void> {
  const inner = normalizeCodewordForTtsRun(codeword);
  if (!inner) {
    remoteTtsVerbose('runTtsCodeword: empty after normalize', { raw: String(codeword).slice(0, 80) });
    return;
  }
  const api = (window as unknown as {
    LecturaNext?: { runCodeword?: (item: unknown) => Promise<void> };
  }).LecturaNext;
  const parsed = parseCodewordInner(inner);
  /**
   * Match deck speaker-notes order in orchestrator: `ttsApi.runCodeword(item)` runs before RevealNotesTTS.
   * If we prefer RevealNotesTTS.runSingleCommand first, a legacy/stub plugin can win and no-op while local clicks use LecturaNext.
   */
  if (api?.runCodeword && parsed) {
    remoteTtsVerbose('runTtsCodeword: LecturaNext.runCodeword', { type: parsed.type });
    await api.runCodeword(parsed);
    return;
  }
  const tts = (window as unknown as {
    RevealNotesTTS?: { runSingleCommand?: (cw: string, slide: Element | null, done: () => void) => void };
  }).RevealNotesTTS;
  if (tts?.runSingleCommand) {
    const slide = typeof Reveal !== 'undefined' ? Reveal.getCurrentSlide?.() || null : null;
    remoteTtsVerbose('runTtsCodeword: RevealNotesTTS.runSingleCommand', { inner: inner.slice(0, 96) });
    tts.runSingleCommand(inner, slide, () => {});
    return;
  }
  if (api?.runCodeword && !parsed) {
    remoteTtsWarn('runTtsCodeword: unknown codeword for LecturaNext.runCodeword', {
      inner: inner.slice(0, 96)
    });
    return;
  }
  remoteTtsWarn(
    'runTtsCodeword: no handler — load notes-tts (RevealNotesTTS.runSingleCommand) or lectura TTS (LecturaNext.runCodeword)',
    { inner: inner.slice(0, 96) }
  );
}

/** Apply a v1 command envelope payload to Reveal (presenter only). */
export function applyRemoteV1CommandToReveal(cmd: RemoteV1CommandPayload): void {
  if (typeof Reveal === 'undefined') return;
  switch (cmd.type) {
    case 'NAVIGATE_NEXT':
      Reveal.next?.();
      return;
    case 'NAVIGATE_PREV':
      Reveal.prev?.();
      return;
    case 'NAVIGATE_UP':
      Reveal.up?.();
      return;
    case 'NAVIGATE_DOWN':
      Reveal.down?.();
      return;
    case 'SET_SLIDE': {
      const s = cmd.slide;
      if (s) Reveal.slide?.(s.h, s.v, s.f);
      return;
    }
    case 'TTS_RUN':
      if (cmd.codeword) {
        runTtsCodeword(cmd.codeword).catch((e: unknown) => {
          remoteTtsWarn('runTtsCodeword failed', { err: String(e) });
        });
      } else {
        remoteTtsVerbose('TTS_RUN without codeword');
      }
      return;
    case 'TTS_SETTINGS':
      if (cmd.lang) {
        const tts = (window as unknown as {
          RevealNotesTTS?: {
            getStoredSettings?: () => Record<string, unknown>;
            setStoredSettings?: (settings: Record<string, unknown>) => void;
          };
        }).RevealNotesTTS;
        if (tts?.setStoredSettings) {
          const current = tts.getStoredSettings?.() || {};
          tts.setStoredSettings({ ...current, lang: cmd.lang });
        }
      }
      return;
    case 'SET_MODE':
    default:
      return;
  }
}
